import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Icon from 'components/AppIcon';
import MonthYearNavigation from 'components/ui/MonthYearNavigation';
import PickDateModal from 'components/ui/PickDateModal';
import UnplannedDatesCell from 'components/ui/UnplannedDatesCell';
import TableSkeleton from 'components/TableSkeleton';
import {
  useAddEmployeeLeaveMutation,
  useGetUnplannedLeavesQuery,
} from 'redux/features/employeeLeaveApi';
import { useBulkProcessDayLogsMutation } from 'redux/features/timeLogApi';
import { getUnplannedDates, getUnplannedState } from 'utils/unplanned';
import AddLeaveModal from '../../holiday-management-employee-view/components/AddLeaveModal';
import DayLogEditModal from '../../dashboard-overview/components/DayLogEditModal';

const UnplannedLeavesTab = ({
  user,
  selectedMonth,
  selectedYear,
  setSelectedMonth,
  setSelectedYear,
}) => {
  const { data, isLoading } = useGetUnplannedLeavesQuery(
    { month: selectedMonth, year: selectedYear, userId: user?.id, limit: 100 },
    { skip: !user?.id, refetchOnMountOrArgChange: true },
  );
  const [addEmployeeLeave, { isLoading: isAddingLeave }] = useAddEmployeeLeaveMutation();
  const [bulkProcessDayLogs] = useBulkProcessDayLogsMutation();

  const [leaveRow, setLeaveRow] = useState(null);
  const [pickRow, setPickRow] = useState(null);
  const [logTarget, setLogTarget] = useState(null); // { rowId, date }

  const rows = useMemo(() => {
    const list = data?.data || data || [];
    return Array.isArray(list) ? list : [];
  }, [data]);

  const handleRequestLeave = async (leaveData) => {
    try {
      await addEmployeeLeave({ ...leaveData, user_id: user?.id, status: 'PENDING' }).unwrap();
      toast.success('Leave request submitted for approval');
      setLeaveRow(null);
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to submit leave request');
    }
  };

  const handleCreateLog = async (payload) => {
    try {
      // unplanned_leave_id: the backend takes this day out of the unplanned leave and tracks the link
      await bulkProcessDayLogs({
        userId: user?.id,
        unplanned_leave_id: logTarget?.rowId,
        ...payload,
      }).unwrap();
      toast.success('Log update request submitted');
      setLogTarget(null);
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to submit log request');
    }
  };

  const handleAddLog = (row) => {
    const dates = getUnplannedDates(row);
    if (dates.length === 1) setLogTarget({ rowId: row.id, date: dates[0] });
    else setPickRow(row);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-gray-600">
          Days you were absent without a leave. Request a leave with the reason, or add a log if you
          worked.
        </p>
        <MonthYearNavigation
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          setSelectedMonth={setSelectedMonth}
          setSelectedYear={setSelectedYear}
        />
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <table className="min-w-full divide-y divide-gray-200">
            <TableSkeleton rows={3} columns={4} />
          </table>
        ) : rows.length === 0 ? (
          <div className="flex h-56 flex-col items-center justify-center text-gray-500">
            <Icon name="CalendarCheck" size={44} className="mb-3 text-gray-400" />
            <p className="text-lg font-medium">No unplanned leaves</p>
            <p className="text-sm">Days without a check-in will appear here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Date', 'Status', 'Reason', 'Action'].map((h) => (
                    <th
                      key={h}
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {rows.map((row) => {
                  const state = getUnplannedState(row);
                  const dates = getUnplannedDates(row);
                  return (
                    <tr key={row.id}>
                      <td className="px-6 py-4">
                        <UnplannedDatesCell dates={dates} className="text-sm text-gray-900" />
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${state.tone}`}
                        >
                          {state.label}
                        </span>
                      </td>
                      <td className="max-w-xs truncate px-6 py-4 text-sm text-gray-600">
                        {row.leave_request?.reason || '—'}
                      </td>
                      <td className="px-6 py-4">
                        {state.key === 'action' ? (
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => setLeaveRow(row)}
                              className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                            >
                              Request Leave
                            </button>
                            <button
                              type="button"
                              onClick={() => handleAddLog(row)}
                              className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                            >
                              Add Log
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {leaveRow && (
        <AddLeaveModal
          isOpen
          onClose={() => setLeaveRow(null)}
          onAddLeave={handleRequestLeave}
          isSubmitting={isAddingLeave}
          selectedEmployeeId={user?.id}
          lockedDates={getUnplannedDates(leaveRow)}
        />
      )}

      <PickDateModal
        isOpen={!!pickRow}
        onClose={() => setPickRow(null)}
        dates={pickRow ? getUnplannedDates(pickRow) : []}
        title="Which day did you work?"
        onSelect={(d) => {
          setLogTarget({ rowId: pickRow.id, date: d });
          setPickRow(null);
        }}
      />

      {logTarget && (
        <DayLogEditModal
          isOpen
          onClose={() => setLogTarget(null)}
          date={logTarget.date}
          logs={[]}
          onSubmit={handleCreateLog}
          user={user}
        />
      )}
    </div>
  );
};

export default UnplannedLeavesTab;
